/**
 * AdrAgentPanel — UI guidata per il workflow Agent ADR/SID
 *
 * Mostra:
 * - Conversazione agent con messaggi strutturati
 * - Campo corrente con select/input appropriato
 * - Bottoni Avanti/Indietro/Salva bozza
 * - Review e generazione
 */

import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
// PongoFin logo - enterprise blue sphere
import {
  ChevronLeft,
  ChevronRight,
  Save,
  FileCheck,
  FileDown,
  RotateCcw,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Bot,
  Play,
  Eye,
  Clock,
  Plus,
  Trash2,
} from 'lucide-react'
import type { AdrState, AdrFieldSchema, AdrOption, AdrFieldError, AdrAgentMessage, AdrTestataValues, AdrRapportoValues, AdrAnagraficaValues } from '../hooks/useAdrAgent'

interface AdrAgentPanelProps {
  state: AdrState
  currentField: string | null
  currentOptions: AdrOption[]
  values: AdrTestataValues
  errors: AdrFieldError[]
  warnings: string[]
  messages: AdrAgentMessage[]
  isLoading: boolean
  personaType: 'pf' | 'pnf' | null
  generatedContent: string | null
  generatedFilename: string | null
  schema: AdrFieldSchema[]
  isIt: boolean
  rapporti: AdrRapportoValues[]
  rapportoTypeOptions: AdrOption[]
  ruoloOptions: AdrOption[]
  // Actions
  onStart: () => void
  onSetField: (field: string, value: string) => void
  onGoBack: () => void
  onGoToReview: () => void
  onGoBackFromRapporti: () => void
  onValidate: () => Promise<boolean>
  onGenerate: () => Promise<unknown>
  onSaveDraft: () => Promise<string | null>
  onReset: () => void
  onRetryFromError: () => void
  onUpdateRapporto: (rapIdx: number, field: keyof AdrRapportoValues, value: unknown) => void
  onAddRapporto: () => void
  onRemoveRapporto: (idx: number) => void
  onUpdateAnagrafica: (rapIdx: number, anagIdx: number, field: keyof AdrAnagraficaValues, value: string) => void
  onAddAnagrafica: (rapIdx: number) => void
  onRemoveAnagrafica: (rapIdx: number, anagIdx: number) => void
}

export default function AdrAgentPanel({
  state,
  currentField,
  currentOptions,
  values,
  errors,
  warnings,
  messages,
  isLoading,
  personaType,
  generatedContent,
  generatedFilename,
  schema,
  isIt,
  rapporti,
  rapportoTypeOptions,
  ruoloOptions,
  onStart,
  onSetField,
  onGoBack,
  onGoToReview,
  onGoBackFromRapporti,
  onValidate,
  onGenerate,
  onSaveDraft,
  onReset,
  onRetryFromError,
  onUpdateRapporto,
  onAddRapporto,
  onRemoveRapporto,
  onUpdateAnagrafica,
  onAddAnagrafica,
  onRemoveAnagrafica,
}: AdrAgentPanelProps) {
  const [fieldInput, setFieldInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Auto-scroll messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus input when field changes
  useEffect(() => {
    if (currentField) {
      setFieldInput(values[currentField as keyof AdrTestataValues] || '')
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [currentField, values])

  const currentFieldSchema = currentField ? schema.find(f => f.field === currentField) : null
  const isSelect = currentFieldSchema?.type === 'select' && currentOptions.length > 0
  const fieldError = errors.find(e => e.field === currentField)

  // Handle submitting a field
  const handleSubmitField = () => {
    if (!currentField) return
    const val = isSelect ? fieldInput : fieldInput.trim()
    if (!val && currentFieldSchema?.required) return
    onSetField(currentField, val)
    setFieldInput('')
  }

  // Download generated file
  const handleDownload = () => {
    if (!generatedContent || !generatedFilename) return
    const blob = new Blob([generatedContent], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = generatedFilename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // ============================================================
  // IDLE STATE — Start button
  // ============================================================
  if (state === 'IDLE') {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500 gap-6 p-8">
        <div className="w-16 h-16 rounded-2xl bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center">
          <Bot className="w-8 h-8 text-teal-600 dark:text-teal-400" />
        </div>
        <div className="text-center space-y-2">
          <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
            {isIt ? 'Agente Comunicazioni AdR' : 'AdR Communication Agent'}
          </p>
          <p className="text-sm max-w-md">
            {isIt
              ? 'L\'agente ti guiderà passo dopo passo nella compilazione della comunicazione all\'Anagrafe dei Rapporti Finanziari.'
              : 'The agent will guide you step by step through filling the Financial Registry communication.'}
          </p>
        </div>
        <button
          onClick={onStart}
          disabled={isLoading}
          className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium flex items-center gap-2 transition-colors shadow-sm"
        >
          {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />}
          {isIt ? 'Avvia Compilazione' : 'Start Workflow'}
        </button>
      </div>
    )
  }

  // ============================================================
  // MAIN PANEL
  // ============================================================
  return (
    <div className="flex flex-col h-full">
      {/* Progress bar */}
      <div className="flex-shrink-0 px-4 py-2 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
          <span className="font-medium">
            {state === 'ADR_STEP' && currentField
              ? `${isIt ? 'Campo' : 'Field'}: ${currentFieldSchema?.label_it || currentField}`
              : state === 'ADR_RAPPORTI' ? (isIt ? 'Rapporti' : 'Accounts')
              : state === 'ADR_REVIEW' ? (isIt ? 'Riepilogo' : 'Review')
              : state === 'ADR_GENERATE' ? (isIt ? 'Generazione...' : 'Generating...')
              : state === 'ADR_DONE' ? (isIt ? 'Completato' : 'Done')
              : state === 'ADR_ERROR_RECOVERABLE' ? (isIt ? 'Errore' : 'Error')
              : ''
            }
          </span>
          <span>
            {state === 'ADR_DONE' && <CheckCircle2 className="w-4 h-4 text-green-500 inline" />}
            {state === 'ADR_ERROR_RECOVERABLE' && <AlertTriangle className="w-4 h-4 text-amber-500 inline" />}
          </span>
        </div>
        {/* Step indicator */}
        {state === 'ADR_STEP' && (
          <div className="w-full bg-gray-200 dark:bg-slate-600 rounded-full h-1.5">
            <div
              className="bg-teal-500 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${getStepProgress(currentField, schema, personaType)}%` }}
            />
          </div>
        )}
        {state === 'ADR_RAPPORTI' && (
          <div className="w-full bg-gray-200 dark:bg-slate-600 rounded-full h-1.5">
            <div className="bg-teal-500 h-1.5 rounded-full transition-all duration-300" style={{ width: '75%' }} />
          </div>
        )}
        {state === 'ADR_REVIEW' && (
          <div className="w-full bg-gray-200 dark:bg-slate-600 rounded-full h-1.5">
            <div className="bg-teal-500 h-1.5 rounded-full transition-all duration-300" style={{ width: '95%' }} />
          </div>
        )}
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
        {messages.map(msg => (
          <div key={msg.id} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.type !== 'user' && (
              <div className="flex-shrink-0 w-7 h-7 mr-2 mt-0.5">
                {msg.type === 'error'
                  ? <XCircle className="w-7 h-7 text-red-400" />
                  : msg.type === 'system'
                    ? <AlertTriangle className="w-7 h-7 text-amber-400" />
                    : <div className="logo-sphere" style={{ width: 28, height: 28 }} />
                }
              </div>
            )}
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
              msg.type === 'user'
                ? 'bg-blue-600 text-white'
                : msg.type === 'error'
                  ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
                  : msg.type === 'system'
                    ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300'
                    : 'bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-800 dark:text-gray-200'
            }`}>
              <div className="whitespace-pre-wrap">{msg.content}</div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* ============================================================ */}
      {/* INPUT AREA — changes based on state */}
      {/* ============================================================ */}

      {/* ADR_STEP — Field input */}
      {state === 'ADR_STEP' && currentField && (
        <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 space-y-3">
          {/* Field label */}
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
            {currentFieldSchema?.label_it || currentField}
            {currentFieldSchema?.required && <span className="text-red-500 ml-0.5">*</span>}
          </label>

          {/* Error */}
          {fieldError && (
            <div className="text-xs text-red-500 flex items-center gap-1">
              <XCircle className="w-3 h-3" /> {fieldError.message}
            </div>
          )}

          {/* Select or Input */}
          {isSelect ? (
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {currentOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => {
                    setFieldInput(opt.value)
                    onSetField(currentField, opt.value)
                  }}
                  disabled={isLoading}
                  className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-colors ${
                    fieldInput === opt.value
                      ? 'bg-teal-50 dark:bg-teal-900/30 border-teal-300 dark:border-teal-600 text-teal-700 dark:text-teal-300'
                      : 'bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600'
                  }`}
                >
                  <span className="font-mono text-xs text-gray-400 mr-2">{opt.value}</span>
                  {opt.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type={currentFieldSchema?.type === 'date' ? 'date' : 'text'}
                value={fieldInput}
                onChange={e => setFieldInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleSubmitField()
                  }
                }}
                maxLength={currentFieldSchema?.maxLength}
                placeholder={currentFieldSchema?.label_it}
                disabled={isLoading}
                className="flex-1 h-11 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              <button
                onClick={handleSubmitField}
                disabled={isLoading || (!fieldInput.trim() && currentFieldSchema?.required)}
                className="px-4 h-11 bg-teal-600 hover:bg-teal-700 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ChevronRight className="w-5 h-5" />}
              </button>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={onGoBack}
              disabled={isLoading}
              className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex items-center gap-1 disabled:opacity-50"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> {isIt ? 'Indietro' : 'Back'}
            </button>
            <button
              onClick={onSaveDraft}
              disabled={isLoading}
              className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 flex items-center gap-1 disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" /> {isIt ? 'Salva bozza' : 'Save draft'}
            </button>
          </div>
        </div>
      )}

      {/* ADR_RAPPORTI — Rapporti form */}
      {state === 'ADR_RAPPORTI' && (
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-y-auto" style={{ maxHeight: '60vh' }}>
          <div className="px-5 py-4 space-y-4">
            {rapporti.map((rap, rapIdx) => (
              <div key={rapIdx} className="border border-gray-200 dark:border-slate-600 rounded-xl overflow-hidden">
                {/* Rapporto header */}
                <div className="bg-gray-50 dark:bg-slate-700/50 px-4 py-2.5 flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                    {isIt ? 'Rapporto' : 'Account'} #{rapIdx + 1}
                  </span>
                  {rapporti.length > 1 && (
                    <button onClick={() => onRemoveRapporto(rapIdx)} className="p-1 text-red-400 hover:text-red-600 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div className="px-4 py-3 space-y-3">
                  {/* Row 1: Tipo Rapporto + Codice Univoco */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-1">{isIt ? 'Tipo Rapporto *' : 'Account Type *'}</label>
                      <select
                        value={rap.tipoRapporto}
                        onChange={e => onUpdateRapporto(rapIdx, 'tipoRapporto', e.target.value)}
                        className="w-full h-9 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 text-xs"
                      >
                        {rapportoTypeOptions.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-1">{isIt ? 'Codice Univoco *' : 'Unique Code *'}</label>
                      <input
                        type="text" maxLength={50}
                        value={rap.codiceUnivocoRapporto}
                        onChange={e => onUpdateRapporto(rapIdx, 'codiceUnivocoRapporto', e.target.value)}
                        placeholder="es. CF-2025-001"
                        className="w-full h-9 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 text-xs font-mono"
                      />
                    </div>
                  </div>
                  {/* Row 2: Data Inizio + Data Fine + CAB */}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-1">{isIt ? 'Data Inizio *' : 'Start Date *'}</label>
                      <input
                        type="date"
                        value={rap.dataInizio}
                        onChange={e => onUpdateRapporto(rapIdx, 'dataInizio', e.target.value)}
                        className="w-full h-9 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-1">{isIt ? 'Data Fine' : 'End Date'}</label>
                      <input
                        type="date"
                        value={rap.dataFine}
                        onChange={e => onUpdateRapporto(rapIdx, 'dataFine', e.target.value)}
                        className="w-full h-9 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-gray-500 dark:text-gray-400 mb-1">CAB</label>
                      <input
                        type="text" maxLength={5}
                        value={rap.cab}
                        onChange={e => onUpdateRapporto(rapIdx, 'cab', e.target.value)}
                        className="w-full h-9 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 text-xs font-mono"
                      />
                    </div>
                  </div>

                  {/* Anagrafiche */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-medium text-gray-600 dark:text-gray-300">
                        {isIt ? 'Anagrafiche' : 'Participants'} ({rap.anagrafiche.length})
                      </span>
                      <button
                        onClick={() => onAddAnagrafica(rapIdx)}
                        className="text-[10px] text-teal-600 dark:text-teal-400 hover:text-teal-700 flex items-center gap-0.5"
                      >
                        <Plus className="w-3 h-3" /> {isIt ? 'Aggiungi' : 'Add'}
                      </button>
                    </div>
                    {rap.anagrafiche.map((anag, anagIdx) => (
                      <div key={anagIdx} className="bg-gray-50 dark:bg-slate-900/50 rounded-lg p-2.5 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-medium text-gray-500">#{anagIdx + 1}</span>
                          {rap.anagrafiche.length > 1 && (
                            <button onClick={() => onRemoveAnagrafica(rapIdx, anagIdx)} className="text-red-400 hover:text-red-600">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[10px] text-gray-400 mb-0.5">CF / P.IVA</label>
                            <input
                              type="text" maxLength={16}
                              value={anag.codiceFiscale}
                              onChange={e => onUpdateAnagrafica(rapIdx, anagIdx, 'codiceFiscale', e.target.value.toUpperCase())}
                              className="w-full h-8 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 text-[11px] font-mono"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] text-gray-400 mb-0.5">{isIt ? 'Ruolo *' : 'Role *'}</label>
                            <select
                              value={anag.codiceRuolo}
                              onChange={e => onUpdateAnagrafica(rapIdx, anagIdx, 'codiceRuolo', e.target.value)}
                              className="w-full h-8 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 text-[11px]"
                            >
                              {ruoloOptions.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[10px] text-gray-400 mb-0.5">{isIt ? 'Cognome' : 'Surname'}</label>
                            <input
                              type="text" maxLength={26}
                              value={anag.cognome}
                              onChange={e => onUpdateAnagrafica(rapIdx, anagIdx, 'cognome', e.target.value)}
                              className="w-full h-8 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 text-[11px]"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] text-gray-400 mb-0.5">{isIt ? 'Nome' : 'Name'}</label>
                            <input
                              type="text" maxLength={25}
                              value={anag.nome}
                              onChange={e => onUpdateAnagrafica(rapIdx, anagIdx, 'nome', e.target.value)}
                              className="w-full h-8 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 text-[11px]"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}

            {/* Add rapporto + actions */}
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={onAddRapporto}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-400 rounded-lg text-xs font-medium hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> {isIt ? 'Aggiungi Rapporto' : 'Add Account'}
              </button>
              <div className="flex-1" />
              <button
                onClick={onGoBackFromRapporti}
                className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex items-center gap-1"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> {isIt ? 'Indietro' : 'Back'}
              </button>
              <button
                onClick={onGoToReview}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-xs font-medium flex items-center gap-1.5 transition-colors"
              >
                {isIt ? 'Vai al Riepilogo' : 'Go to Review'} <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADR_REVIEW — Review + action buttons */}
      {state === 'ADR_REVIEW' && (
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-y-auto" style={{ maxHeight: '55vh' }}>
          <div className="px-5 py-4 space-y-3">
          {/* Testata Summary */}
          <div className="bg-gray-50 dark:bg-slate-900 rounded-xl p-3 space-y-1.5">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1">
              <Eye className="w-3.5 h-3.5" /> {isIt ? 'Testata' : 'Header'}
            </div>
            {getFilledFields(values, schema, personaType).map(({ label, value }) => (
              <div key={label} className="flex justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-400">{label}</span>
                <span className="text-gray-800 dark:text-gray-200 font-medium">{value}</span>
              </div>
            ))}
          </div>

          {/* Rapporti Summary */}
          {rapporti.length > 0 && rapporti.some(r => r.codiceUnivocoRapporto || r.tipoRapporto) && (
            <div className="bg-gray-50 dark:bg-slate-900 rounded-xl p-3 space-y-2">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <Eye className="w-3.5 h-3.5" /> {isIt ? `Rapporti (${rapporti.length})` : `Accounts (${rapporti.length})`}
              </div>
              {rapporti.map((r, i) => {
                const typeLabel = rapportoTypeOptions.find(o => o.value === r.tipoRapporto)?.label || r.tipoRapporto
                return (
                  <div key={i} className="border-t border-gray-200 dark:border-slate-700 pt-1.5 space-y-0.5">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">#{i + 1} {isIt ? 'Tipo' : 'Type'}</span>
                      <span className="text-gray-800 dark:text-gray-200 font-medium text-right truncate ml-2">{typeLabel}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">{isIt ? 'Codice' : 'Code'}</span>
                      <span className="text-gray-800 dark:text-gray-200 font-mono">{r.codiceUnivocoRapporto || '—'}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">{isIt ? 'Periodo' : 'Period'}</span>
                      <span className="text-gray-800 dark:text-gray-200">{r.dataInizio || '—'} → {r.dataFine || '—'}</span>
                    </div>
                    <div className="text-[10px] text-gray-400">{r.anagrafiche.length} {isIt ? 'anagrafiche' : 'participants'}</div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Warnings */}
          {warnings.length > 0 && (
            <div className="text-xs text-amber-600 dark:text-amber-400 space-y-1">
              {warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-1">
                  <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> {w}
                </div>
              ))}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={async () => {
                const valid = await onValidate()
                if (valid) await onGenerate()
              }}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCheck className="w-4 h-4" />}
              {isIt ? 'Valida e Genera' : 'Validate & Generate'}
            </button>
            <button
              onClick={onSaveDraft}
              disabled={isLoading}
              className="px-4 py-2.5 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-xl text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
            >
              <Save className="w-4 h-4" /> {isIt ? 'Salva' : 'Save'}
            </button>
            <button
              onClick={onReset}
              disabled={isLoading}
              className="px-4 py-2.5 text-gray-500 dark:text-gray-400 hover:text-red-500 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
            >
              <RotateCcw className="w-4 h-4" /> {isIt ? 'Ricomincia' : 'Reset'}
            </button>
          </div>
          </div>
        </div>
      )}

      {/* ADR_DONE — Download */}
      {state === 'ADR_DONE' && (
        <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 space-y-3">
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm font-medium">
            <CheckCircle2 className="w-5 h-5" />
            {isIt ? 'Comunicazione generata con successo!' : 'Communication generated successfully!'}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleDownload}
              className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors"
            >
              <FileDown className="w-4 h-4" /> {isIt ? 'Scarica TXT' : 'Download TXT'}
            </button>
            <button
              onClick={onSaveDraft}
              disabled={isLoading}
              className="px-4 py-2.5 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-xl text-sm font-medium flex items-center justify-center gap-2 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
            >
              <Save className="w-4 h-4" /> {isIt ? 'Salva' : 'Save'}
            </button>
            <button
              onClick={onReset}
              className="px-4 py-2.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-xl text-sm flex items-center justify-center gap-2 transition-colors"
            >
              <RotateCcw className="w-4 h-4" /> {isIt ? 'Nuova' : 'New'}
            </button>
          </div>
          <Link
            to="/sid/storico"
            className="flex items-center justify-center gap-2 text-sm text-amber-700 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 font-medium transition-colors mt-1"
          >
            <Clock className="w-4 h-4" />
            {isIt ? 'Vai allo Storico per gestire i download' : 'Go to History to manage downloads'}
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {/* ADR_ERROR_RECOVERABLE — Show errors + retry */}
      {state === 'ADR_ERROR_RECOVERABLE' && (
        <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 space-y-3">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3 space-y-1">
            <div className="text-sm font-medium text-red-700 dark:text-red-300 flex items-center gap-1.5">
              <XCircle className="w-4 h-4" /> {isIt ? 'Errori riscontrati' : 'Errors found'}
            </div>
            {errors.map((e, i) => (
              <div key={i} className="text-xs text-red-600 dark:text-red-400 ml-5">
                • {e.message}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onRetryFromError}
              className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors"
            >
              <RotateCcw className="w-4 h-4" /> {isIt ? 'Correggi e riprova' : 'Fix and retry'}
            </button>
            <button
              onClick={onReset}
              className="px-4 py-2.5 text-gray-500 dark:text-gray-400 hover:text-red-500 rounded-xl text-sm flex items-center justify-center gap-2 transition-colors"
            >
              <RotateCcw className="w-4 h-4" /> {isIt ? 'Ricomincia' : 'Reset'}
            </button>
          </div>
        </div>
      )}

      {/* ADR_GENERATE — Loading */}
      {state === 'ADR_GENERATE' && (
        <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
          <div className="flex items-center gap-3 justify-center text-teal-600 dark:text-teal-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm font-medium">{isIt ? 'Generazione file in corso...' : 'Generating file...'}</span>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================
// HELPERS
// ============================================================

function getStepProgress(currentField: string | null, schema: AdrFieldSchema[], personaType: 'pf' | 'pnf' | null): number {
  const requiredFields = schema
    .filter(f => f.required)
    .filter(f => {
      if (personaType === 'pf' && f.group === 'persona_non_fisica') return false
      if (personaType === 'pnf' && f.group === 'persona_fisica') return false
      return true
    })
  if (!currentField || requiredFields.length === 0) return 0
  const idx = requiredFields.findIndex(f => f.field === currentField)
  if (idx < 0) return 50
  return Math.round(((idx + 1) / requiredFields.length) * 100)
}

function getFilledFields(
  values: AdrTestataValues,
  schema: AdrFieldSchema[],
  personaType: 'pf' | 'pnf' | null,
): { label: string; value: string }[] {
  const result: { label: string; value: string }[] = []
  for (const field of schema) {
    if (personaType === 'pf' && field.group === 'persona_non_fisica') continue
    if (personaType === 'pnf' && field.group === 'persona_fisica') continue
    const val = values[field.field as keyof AdrTestataValues]
    if (val) {
      result.push({ label: field.label_it, value: val })
    }
  }
  return result
}
